import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import './CrudPage.css';
import './UserManagementPage.css';

interface User {
  id: string;
  email: string;
  full_name: string;
  organization: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

interface UserFormData {
  email: string;
  password: string;
  full_name: string;
  organization: string;
}

const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    full_name: '',
    organization: ''
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${API_ENDPOINTS.AUTH.USERS}?page=${currentPage}&limit=10&search=${encodeURIComponent(searchTerm)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUsers(data.users);
          setTotalPages(data.total_pages);
        } else {
          setError(data.message || 'Failed to fetch users');
        }
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, fetchUsers]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.full_name) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(API_ENDPOINTS.AUTH.USERS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowCreateForm(false);
        setFormData({ email: '', password: '', full_name: '', organization: '' });
        fetchUsers();
        setError(null);
      } else {
        setError(data.detail || data.message || 'Failed to create user');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Create user error:', err);
    }
  };

  const handleUpdateUser = async (userId: string, updates: Partial<User>) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_ENDPOINTS.AUTH.USERS}/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        fetchUsers();
        setEditingUser(null);
        setError(null);
      } else {
        setError(data.detail || data.message || 'Failed to update user');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Update user error:', err);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Are you sure you want to delete user: ${userEmail}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_ENDPOINTS.AUTH.USERS}/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        fetchUsers();
        setError(null);
      } else {
        setError(data.detail || data.message || 'Failed to delete user');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Delete user error:', err);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resetPasswordData.newPassword || !resetPasswordData.confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (resetPasswordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_ENDPOINTS.AUTH.USERS}/${resetPasswordUser!.id}/reset-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ new_password: resetPasswordData.newPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResetPasswordUser(null);
        setResetPasswordData({ newPassword: '', confirmPassword: '' });
        setError(null);
      } else {
        setError(data.detail || data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Reset password error:', err);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="crud-page">
        <div className="crud-loading">
          <div className="crud-spinner"></div>
          <p>Loading users…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crud-page um-page">
      <div className="um-toolbar">
        <input
          type="text"
          placeholder="Search users…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="crud-input um-search-input"
        />
        <button
          className="crud-btn crud-btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          <span className="crud-btn-icon">+</span> Add User
        </button>
      </div>

      {/* Toasts */}
      {error && (
        <div className="crud-toast crud-toast-error">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Users Table */}
      <div className="crud-table-wrap">
        {users.length === 0 && !loading ? (
          <div className="crud-empty">
            <div className="crud-empty-icon">👤</div>
            <h3>No users found</h3>
            <p>No users match your search criteria.</p>
            <button className="crud-btn crud-btn-primary" onClick={() => setShowCreateForm(true)}>
              + Add User
            </button>
          </div>
        ) : (
        <table className="crud-table um-users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="um-user-info">
                    <div className="um-user-avatar">
                      {user.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="um-user-name">{user.full_name}</div>
                      <div className="um-user-email">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`um-role-badge ${user.role}`}>
                    {user.role === 'admin' ? 'Administrator' : 'User'}
                  </span>
                </td>
                <td>{user.organization || '-'}</td>
                <td>
                  <span className={`um-status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{formatDate(user.created_at)}</td>
                <td>{user.last_login ? formatDate(user.last_login) : 'Never'}</td>
                <td>
                  <div className="um-action-buttons">
                    <button
                      className="um-action-btn edit"
                      onClick={() => setEditingUser(user)}
                      title="Edit User"
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4A2 2 0 0 0 2 6V18A2 2 0 0 0 4 20H18A2 2 0 0 0 20 18V13" stroke="currentColor" strokeWidth="2"/>
                        <path d="M18.5 2.5A2.121 2.121 0 0 1 21 4.621L20.379 5.243L18.757 3.621L18.5 2.5Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M15 7L17 9" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                    <button
                      className="um-action-btn reset-password"
                      onClick={() => setResetPasswordUser(user)}
                      title="Reset Password"
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="16" r="1" stroke="currentColor" strokeWidth="2"/>
                        <path d="M7 11V7A5 5 0 0 1 17 7V11" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                    {currentUser && user.id !== (currentUser as any).id && (
                      <button
                        className="um-action-btn delete"
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        title="Delete User"
                      >
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4A2 2 0 0 1 10 2H14A2 2 0 0 1 16 4V6M19 6V20A2 2 0 0 1 17 22H7A2 2 0 0 1 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="crud-pagination">
          <button
            className="crud-pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="crud-pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="crud-pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="crud-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="crud-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crud-modal-header">
              <h2>Create New User</h2>
              <button className="crud-modal-close" onClick={() => setShowCreateForm(false)}>&times;</button>
            </div>

            <form onSubmit={handleCreateUser} className="um-form">
              <div className="crud-form-section">
                <label className="crud-label" htmlFor="fullName">Full Name *</label>
                <input className="crud-input" type="text" id="fullName" value={formData.full_name} onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))} required />
              </div>
              <div className="crud-form-section">
                <label className="crud-label" htmlFor="email">Email Address *</label>
                <input className="crud-input" type="email" id="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required />
              </div>
              <div className="crud-form-section">
                <label className="crud-label" htmlFor="organization">Organization</label>
                <input className="crud-input" type="text" id="organization" value={formData.organization} onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="crud-form-section">
                <label className="crud-label" htmlFor="password">Password *</label>
                <input className="crud-input" type="password" id="password" value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} required minLength={6} />
              </div>
              <div className="crud-modal-actions">
                <button type="button" onClick={() => setShowCreateForm(false)} className="crud-btn crud-btn-ghost">Cancel</button>
                <button type="submit" className="crud-btn crud-btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="crud-overlay" onClick={() => setEditingUser(null)}>
          <div className="crud-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crud-modal-header">
              <h2>Edit User</h2>
              <button className="crud-modal-close" onClick={() => setEditingUser(null)}>&times;</button>
            </div>
            <div className="um-edit-form">
              <div className="crud-form-section">
                <label className="crud-label">Full Name</label>
                <input className="crud-input" type="text" value={editingUser.full_name} onChange={(e) => setEditingUser(prev => prev ? { ...prev, full_name: e.target.value } : null)} />
              </div>
              <div className="crud-form-section">
                <label className="crud-label">Organization</label>
                <input className="crud-input" type="text" value={editingUser.organization} onChange={(e) => setEditingUser(prev => prev ? { ...prev, organization: e.target.value } : null)} />
              </div>
              <div className="crud-form-section">
                <label className="crud-label">Role</label>
                <select className="crud-input" value={editingUser.role} onChange={(e) => setEditingUser(prev => prev ? { ...prev, role: e.target.value } : null)}>
                  <option value="user">User</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              <div className="crud-form-section">
                <label className="um-checkbox-label">
                  <input type="checkbox" checked={editingUser.is_active} onChange={(e) => setEditingUser(prev => prev ? { ...prev, is_active: e.target.checked } : null)} />
                  <span>Account Active</span>
                </label>
              </div>
              <div className="crud-modal-actions">
                <button type="button" onClick={() => setEditingUser(null)} className="crud-btn crud-btn-ghost">Cancel</button>
                <button type="button" onClick={() => handleUpdateUser(editingUser.id, { full_name: editingUser.full_name, organization: editingUser.organization, role: editingUser.role, is_active: editingUser.is_active })} className="crud-btn crud-btn-primary">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div className="crud-overlay" onClick={() => setResetPasswordUser(null)}>
          <div className="crud-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crud-modal-header">
              <h2>Reset Password</h2>
              <button className="crud-modal-close" onClick={() => setResetPasswordUser(null)}>&times;</button>
            </div>
            <p className="um-modal-subtitle">Reset password for: <strong>{resetPasswordUser.email}</strong></p>
            <form onSubmit={handleResetPassword} className="um-form">
              <div className="crud-form-section">
                <label className="crud-label" htmlFor="newPassword">New Password *</label>
                <input className="crud-input" type="password" id="newPassword" value={resetPasswordData.newPassword} onChange={(e) => setResetPasswordData(prev => ({ ...prev, newPassword: e.target.value }))} required minLength={6} />
              </div>
              <div className="crud-form-section">
                <label className="crud-label" htmlFor="confirmPassword">Confirm Password *</label>
                <input className="crud-input" type="password" id="confirmPassword" value={resetPasswordData.confirmPassword} onChange={(e) => setResetPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))} required minLength={6} />
              </div>
              <div className="crud-modal-actions">
                <button type="button" onClick={() => setResetPasswordUser(null)} className="crud-btn crud-btn-ghost">Cancel</button>
                <button type="submit" className="crud-btn crud-btn-primary">Reset Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
